/**
 * Financial Aid Value Objects: Family Information
 *
 * Sections 1-3: Family Information and Parent/Guardian Information
 *
 * @module financial-aid-domain/value-objects/financial-aid/family-info
 */
import { z } from 'zod';
import { AddressSchema, EmailSchema, PhoneNumberSchema } from '@triedge/domain-core';

const BOUNDED_TEXT_MAX_LENGTH = 4000;
const OptionalBoundedTextSchema = z.string().trim().max(BOUNDED_TEXT_MAX_LENGTH).nullable().optional();

export const formatMaskedSocialSecurityLast4 = (
  value: unknown,
  fallback = 'Not provided',
): string => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 0) {
    return fallback;
  }
  if (digits.length !== 4) {
    return 'Stored value unreadable - contact support';
  }
  return `xxx-xx-${digits}`;
};

export const formatFinancialAidSocialSecurityNumberInput = (value: unknown): string => {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export const isValidFinancialAidSocialSecurityNumber = (value: unknown): boolean =>
  /^\d{3}-\d{2}-\d{4}$/.test(String(value ?? '').trim());

export const readFinancialAidSocialSecurityLast4 = (value: unknown): string | null => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
};

const INVALID_ERROR_TEXT = '[object Object]';

export type FinancialAidActionErrorReport = {
  userMessage: string;
  technicalMessage: string | null;
  technicalReport: string | null;
  code?: string | null;
};

export const FINANCIAL_AID_CYCLE_SETTINGS_UNAVAILABLE_CODE =
  'FINANCIAL_AID_CYCLE_SETTINGS_UNAVAILABLE';

export const FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE =
  'FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE';

export const FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_ERROR_TYPE =
  'FinancialAidInviteConfirmationUnavailableError';

export const FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODES = [
  FINANCIAL_AID_CYCLE_SETTINGS_UNAVAILABLE_CODE,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_ERROR_TYPE,
] as const;

export const FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_MESSAGE =
  'Unable to confirm this financial aid invitation right now. Please try again, or contact the school office.';

export const isFinancialAidInviteConfirmationUnavailableCode = (value: unknown): boolean => (
  typeof value === 'string'
  && FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODES.includes(
    value as typeof FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODES[number],
  )
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readErrorText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed && trimmed !== INVALID_ERROR_TEXT ? trimmed : null;
};

const looksLikeBackendSchemaIssues = (value: unknown): boolean => (
  Array.isArray(value)
  && value.some((issue) => (
    isRecord(issue)
    && (typeof issue.code === 'string' || Array.isArray(issue.path))
    && typeof issue.message === 'string'
  ))
);

const readBackendSchemaIssueSummary = (message: string | null): string | null => {
  if (!message) return null;
  try {
    const parsed = JSON.parse(message);
    if (!looksLikeBackendSchemaIssues(parsed)) return null;
    const issues = Array.isArray(parsed) ? parsed : [];
    return issues
      .filter((issue): issue is Record<string, unknown> => isRecord(issue))
      .slice(0, 8)
      .map((issue: Record<string, unknown>) => (
        Array.isArray(issue.path) ? issue.path.map(String).join('.') : 'unknown path'
      ))
      .join('; ');
  } catch {
    return null;
  }
};

const containsSensitiveInlineValue = (message: string): boolean => (
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(message)
  || /\b\d{3}[-.\s]?\d{2}[-.\s]?\d{4}\b/.test(message)
  || /\b\d{10,}\b/.test(message)
);

const sanitizeActionErrorMessage = (message: string | null): string | null => {
  if (!message) return null;
  if (
    /Number\.MAX_SAFE_INTEGER/i.test(message)
    || /NumberValue from @aws-sdk\/lib-dynamodb/i.test(message)
  ) {
    return 'One or more dollar amounts is too large. Please review the highlighted amount fields before submitting.';
  }
  if (containsSensitiveInlineValue(message)) return null;
  try {
    const parsed = JSON.parse(message);
    if (looksLikeBackendSchemaIssues(parsed)) {
      return 'Please review the highlighted fields before submitting.';
    }
  } catch {
    return message;
  }
  return message;
};

const readNestedActionErrorMessage = (value: unknown): string | null => {
  if (!isRecord(value)) return null;
  return readErrorText(value.message)
    ?? (isRecord(value.data) ? readErrorText(value.data.message) : null)
    ?? (isRecord(value.error) ? readErrorText(value.error.message) : null);
};

const findActionErrorSupportId = (value: unknown, depth = 0): string | null => {
  if (depth > 3 || !isRecord(value)) return null;
  const candidateKeys = [
    'correlationId',
    'requestId',
    'requestID',
    'traceId',
    'x-amzn-requestid',
    'extendedRequestId',
  ];
  for (const key of candidateKeys) {
    const candidate = readErrorText(value[key]);
    if (candidate) return candidate;
  }
  for (const nestedKey of ['$metadata', 'metadata', 'data', 'error']) {
    const candidate = findActionErrorSupportId(value[nestedKey], depth + 1);
    if (candidate) return candidate;
  }
  return null;
};

const readActionErrorCode = (value: unknown, depth = 0): string | null => {
  if (depth > 3 || !isRecord(value)) return null;
  const candidateKeys = [
    'code',
    'errorCode',
    'errorType',
  ];
  for (const key of candidateKeys) {
    const candidate = readErrorText(value[key]);
    if (!candidate) continue;
    if (candidate === 'FinancialAidCycleSettingsUnavailableError') {
      return FINANCIAL_AID_CYCLE_SETTINGS_UNAVAILABLE_CODE;
    }
    if (candidate === FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_ERROR_TYPE) {
      return FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE;
    }
    return candidate;
  }
  for (const nestedKey of ['data', 'error', 'extensions', 'cause']) {
    const candidate = readActionErrorCode(value[nestedKey], depth + 1);
    if (candidate) return candidate;
  }
  return null;
};

const classifyActionErrorCode = (message: string | null): string => {
  if (!message) return 'ACTION_FAILED';
  if (
    /Number\.MAX_SAFE_INTEGER/i.test(message)
    || /NumberValue from @aws-sdk\/lib-dynamodb/i.test(message)
  ) {
    return 'NUMERIC_VALUE_OUT_OF_RANGE';
  }
  if (readBackendSchemaIssueSummary(message)) return 'BACKEND_SCHEMA_VALIDATION_FAILED';
  if (/upload/i.test(message)) return 'DOCUMENT_UPLOAD_FAILED';
  return 'ACTION_FAILED';
};

const readActionErrorTechnicalMessage = (error: unknown, code: string | null = null): string => {
  const buildSupportReport = (message: string | null, supportId: string | null): string => {
    const schemaSummary = readBackendSchemaIssueSummary(message);
    return [
      `Error code: ${code ?? classifyActionErrorCode(message)}`,
      supportId ? `Support id: ${supportId}` : 'Support id: not provided by backend',
      schemaSummary ? `Validation fields: ${schemaSummary}` : null,
      `Reported at: ${new Date().toISOString()}`,
    ].filter(Boolean).join('\n');
  };

  if (error instanceof Error) {
    return buildSupportReport(readErrorText(error.message), findActionErrorSupportId(error));
  }
  const directOrNested = readErrorText(error) ?? readNestedActionErrorMessage(error);
  return buildSupportReport(directOrNested, findActionErrorSupportId(error));
};

export const createFinancialAidActionErrorReport = (
  userMessage: string,
  technicalMessage: string | null = null,
  code: string | null = null,
): FinancialAidActionErrorReport => {
  const cleanUserMessage = readErrorText(userMessage) ?? 'Unable to complete this action right now.';
  const cleanTechnicalMessage = readErrorText(technicalMessage);
  const cleanCode = readErrorText(code);
  return {
    userMessage: cleanUserMessage,
    technicalMessage: cleanTechnicalMessage,
    code: cleanCode,
    technicalReport: cleanTechnicalMessage
      ? [
        'Parent financial aid action failed.',
        `User-facing message: ${cleanUserMessage}`,
        cleanCode ? `Error code: ${cleanCode}` : null,
        `Technical detail: ${cleanTechnicalMessage}`,
      ].filter(Boolean).join('\n')
      : null,
  };
};

export const readFinancialAidActionErrorReport = (
  error: unknown,
  fallback: string,
): FinancialAidActionErrorReport => {
  const rawMessage = error instanceof Error
    ? readErrorText(error.message)
    : (readErrorText(error) ?? readNestedActionErrorMessage(error));
  const code = readActionErrorCode(error);
  const userMessage = sanitizeActionErrorMessage(rawMessage) ?? fallback;
  return createFinancialAidActionErrorReport(userMessage, readActionErrorTechnicalMessage(error, code), code);
};

export const readFinancialAidActionErrorMessage = (
  error: unknown,
  fallback: string,
): string => readFinancialAidActionErrorReport(error, fallback).userMessage;

// ============================================================================
// Section 1: Family Information
// ============================================================================

const EmptyInviteAddressSchema = z.object({
  street: z.literal(''),
  city: z.literal(''),
  state: z.literal(''),
  zipCode: z.literal(''),
});

export const FamilyInformationSchema = z.object({
  familyName: z.string().min(1),
  // Invite-created records start before the family has supplied an address.
  // Submission validation still enforces complete family details later.
  homeAddress: z.union([AddressSchema, EmptyInviteAddressSchema]),
  homePhone: z.string().nullable().optional(),
  maritalStatus: z.enum(['married', 'single', 'divorced', 'separated', 'widowed']),
  dateOfMarriage: z.number().int().nullable().optional(), // Unix timestamp
  numberOfDependents: z.number().int().nonnegative(),
  numberOfYstStudents: z.number().int().nonnegative(),
  numberOfPreschoolStudents: z.number().int().nonnegative().default(0),
  householdSize: z.number().int().nonnegative(),
  synagogue: OptionalBoundedTextSchema,
  rabbi: OptionalBoundedTextSchema,
});
export type FamilyInformation = z.infer<typeof FamilyInformationSchema>;

// ============================================================================
// Sections 2 & 3: Parent/Guardian Information
// ============================================================================

const EmptyInviteParentNameSchema = z.literal('');
const EmptyInvitePhoneSchema = z.literal('');

export const ParentGuardianInfoSchema = z.object({
  title: z.string().nullable().optional(),
  // Invite-created applications can exist before the parent has supplied contact details.
  // Submission validation still requires complete parent details later in the flow.
  firstName: z.union([z.string().min(1), EmptyInviteParentNameSchema]),
  lastName: z.string().min(1),
  hebrewName: z.string().nullable().optional(),
  relationship: z.enum(['father', 'mother', 'stepfather', 'stepmother', 'guardian', 'guardian1', 'guardian2']),
  displayRole: z.enum(['father', 'mother', 'grandfather', 'grandmother', 'legal_guardian', 'other']).nullable().optional(),
  otherRoleLabel: z.string().nullable().optional(),
  email: EmailSchema,
  address: AddressSchema.nullable().optional(),
  cellPhone: z.union([PhoneNumberSchema, EmptyInvitePhoneSchema]),
  homePhone: z.string().nullable().optional(),
  workPhone: z.string().nullable().optional(),
  dateOfBirth: z.number().int().nullable().optional(), // Unix timestamp
  socialSecurityLast4: z.string().length(4).nullable().optional(),
  citizenship: z.string().default('US'),
  employer: z.string().nullable().optional(),
  occupation: z.string().nullable().optional(),
  yearsEmployed: z.number().nonnegative().nullable().optional(),
  isUnemployed: z.boolean().default(false),
  unemploymentDuration: z.string().nullable().optional(),
  unemploymentWeeklyAmount: z.union([z.number(), z.string()]).nullable().optional(),
  educationLevel: z.enum(['high_school', 'some_college', 'associates', 'bachelors', 'masters', 'doctorate', 'other']).nullable().optional(),
});
export type ParentGuardianInfo = z.infer<typeof ParentGuardianInfoSchema>;

export const DraftSecondaryParentGuardianInfoSchema = ParentGuardianInfoSchema.extend({
  lastName: z.union([z.string().min(1), EmptyInviteParentNameSchema]),
  email: z.union([EmailSchema, EmptyInviteParentNameSchema]),
  cellPhone: z.union([PhoneNumberSchema, EmptyInvitePhoneSchema]).optional(),
});
export type DraftSecondaryParentGuardianInfo = z.infer<typeof DraftSecondaryParentGuardianInfoSchema>;
